import React, { useCallback, useEffect, useRef } from "react";
import SearchResultsInfo from "./SearchResultsInfo";

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  entityName: string; // 'exercises', 'programs', 'users', etc.
  isSearchMode?: boolean;
  onPageChange: (page: number) => void;
  loading?: boolean;
  // Scrolls here on page change instead of the top of the page. Set for
  // sections embedded in a longer page; unset on full-page table views.
  scrollTargetRef?: React.RefObject<HTMLElement | null>;
}

// Clears the sticky navbar (min-height 97px, offset top-4)
const SCROLL_TARGET_OFFSET = 120;

const Pagination: React.FC<PaginationProps> = ({
  page,
  limit,
  total,
  totalPages,
  entityName,
  isSearchMode = false,
  onPageChange,
  loading = false,
  scrollTargetRef,
}) => {
  const requestedPage = useRef<number | null>(null);

  const scrollIntoPlace = useCallback(() => {
    const target = scrollTargetRef?.current;

    if (target) {
      const top =
        target.getBoundingClientRect().top +
        window.scrollY -
        SCROLL_TARGET_OFFSET;
      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [scrollTargetRef]);

  const handlePageChange = (newPage: number) => {
    requestedPage.current = newPage;
    scrollIntoPlace();
    onPageChange(newPage);
  };

  // The click-time scroll is a smooth animation that the loading swap can
  // cancel mid-flight (the list collapses to a spinner and this component
  // unrenders). Re-assert it once the requested page has rendered, when the
  // layout is final. Page changes not initiated here (e.g. a filter resetting
  // to page 1) never scroll.
  useEffect(() => {
    if (!loading && requestedPage.current === page) {
      requestedPage.current = null;
      scrollIntoPlace();
    }
  }, [page, loading, scrollIntoPlace]);

  if (loading) {
    return null;
  }

  // Don't show pagination if only one page
  if (totalPages <= 1 && !isSearchMode) {
    return null;
  }

  // Show results info without navigation in search mode
  if (totalPages <= 1 && isSearchMode) {
    return (
      <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
        <SearchResultsInfo
          page={page}
          limit={limit}
          total={total}
          entityName={entityName}
          isSearchMode={isSearchMode}
        />
      </div>
    );
  }

  return (
    <div className="mt-6 flex items-center justify-between pt-6">
      {/* Mobile pagination */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Next
        </button>
      </div>

      {/* Desktop pagination */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <SearchResultsInfo
          page={page}
          limit={limit}
          total={total}
          entityName={entityName}
          isSearchMode={isSearchMode}
        />
        <div>
          <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm">
            {/* First Page Button */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              title="First page"
            >
              ««
            </button>
            {/* Previous Page Button */}
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Previous page"
            >
              ‹
            </button>
            {/* Page numbers */}
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                    page === pageNum
                      ? "z-10 border-biafit-primary bg-biafit-primary text-white"
                      : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            {/* Next Page Button */}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="relative inline-flex items-center border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Next page"
            >
              ›
            </button>
            {/* Last Page Button */}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
              className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Last page"
            >
              »»
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
